import { Component } from 'react';
import text from 'I18n/utils/unescapedText';
import EditFormControl from 'ContentComponents/components/cmv2/EditFormControl';
import EditFormLabel from 'ContentComponents/components/cmv2/EditFormLabel';
import SanitizedLabel from 'ContentComponents/components/cmv2/SanitizedLabel';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { resolveFieldBindingProps } from 'ContentComponents/components/cmv2/utils/bindingResolution';
import { isValueEmpty, hideFieldLabel } from 'ContentComponents/components/cmv2/utils';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { getDataTokenPath } from 'ContentComponents/components/cmv2/utils/dataTokens';
import { Flex } from 'foundations-components/components/Flex';
import { HubDBIcon } from 'foundations-assets/icons/components/HubDBIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.${key}`;
class EditFormField extends Component {
  constructor(props) {
    super(props);
    this.setError = this.setError.bind(this);
    this.onFieldChange = this.onFieldChange.bind(this);
    this.onValidateRequiredValue = this.onValidateRequiredValue.bind(this);
    this.state = {
      error: null,
      missingRequiredValue: false
    };
  }
  componentDidMount() {
    if (this.props.validateOnMount) {
      this.onValidateRequiredValue();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      breakpoint,
      validateOnMount
    } = this.props;
    if (prevProps.breakpoint !== breakpoint || !prevProps.validateOnMount && validateOnMount) {
      this.onValidateRequiredValue();
    }
  }
  get shouldRenderDataToken() {
    const {
      field
    } = this.props;
    return Boolean(field.dataTokenValue);
  }
  setError(message) {
    this.setState({
      error: message
    });
  }
  onFieldChange(value) {
    const {
      onFieldChange,
      fieldKey,
      field
    } = this.props;
    onFieldChange(fieldKey, value);
    if (this.state.missingRequiredValue && !isValueEmpty(field, value)) {
      this.setState({
        error: null,
        missingRequiredValue: false
      });
    }
  }
  onValidateRequiredValue(value = this.props.value) {
    const {
      field
    } = this.props;
    if (!field.required) {
      return;
    }
    if (isValueEmpty(field, value)) {
      this.setState({
        error: text(i18nKey('requiredMessage')),
        missingRequiredValue: true
      });
    } else if (this.state.missingRequiredValue) {
      this.setState({
        error: null,
        missingRequiredValue: false
      });
    }
  }
  renderDisabledAlert() {
    const {
      disabled,
      field
    } = this.props;
    const {
      disabled_controls
    } = field;
    if (!disabled || !(disabled_controls !== null && disabled_controls !== void 0 && disabled_controls.message)) return null;
    return /*#__PURE__*/_jsx(Alert, {
      type: "tip",
      marginBlockEnd: "300",
      children: /*#__PURE__*/_jsx(SanitizedLabel, {
        label: disabled_controls.message,
        useStrict: true
      })
    });
  }
  renderDataTokenViewer() {
    const {
      dataTokenValue
    } = this.props.field;
    return /*#__PURE__*/_jsx(Tag, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "100",
        children: [/*#__PURE__*/_jsx(HubDBIcon, {
          size: "300"
        }), getDataTokenPath(dataTokenValue)]
      })
    });
  }
  renderField(renderedLabel) {
    const {
      bindingContext,
      componentsByType,
      contentAssistanceEnabled,
      contentTypes,
      currentFieldKey,
      disabled,
      editor,
      field,
      fieldKey,
      flush,
      gates,
      highlightedField,
      hostTemplateTypes,
      inheritingFieldsUtils,
      isDefaultValue,
      isEmailOrAnyType,
      isInEmail,
      isInNewEditor,
      isPublicContent,
      modulePath,
      onRedirect,
      readOnly,
      resourceProps,
      scopes,
      scrollToField,
      setFieldKey,
      trackFieldAction,
      uniqueKey,
      useOnDark,
      uploadedFileAccess,
      value
    } = this.props;
    const {
      error
    } = this.state;
    const FieldComponent = componentsByType[field.type];
    if (highlightedField.length > 0 && highlightedField[0] === field.name) {
      highlightedField.shift();
    }
    if (this.shouldRenderDataToken) {
      return this.renderDataTokenViewer();
    }
    if (!FieldComponent) {
      return null;
    }
    const bindingProps = resolveFieldBindingProps(field, fieldKey, bindingContext);
    return /*#__PURE__*/_jsx(FieldComponent, Object.assign({}, field, resourceProps, bindingProps, {
      componentsByType: componentsByType,
      contentAssistanceEnabled: contentAssistanceEnabled,
      contentTypes: contentTypes,
      currentFieldKey: currentFieldKey,
      editor: editor,
      error: !!error,
      fieldKey: fieldKey,
      flush: flush,
      gates: gates,
      hostTemplateTypes: hostTemplateTypes,
      inheritingFieldsUtils: inheritingFieldsUtils,
      isDefaultValue: isDefaultValue,
      isEmailOrAnyType: isEmailOrAnyType,
      isInEmail: isInEmail,
      isInNewEditor: isInNewEditor,
      isPublicContent: isPublicContent,
      label: renderedLabel,
      modulePath: modulePath,
      onFieldChange: this.onFieldChange,
      onRedirect: onRedirect,
      onValidateRequiredValue: this.onValidateRequiredValue,
      readOnly: readOnly || disabled,
      scopes: scopes,
      scrollToField: scrollToField,
      setError: this.setError,
      setFieldKey: setFieldKey,
      highlightedField: highlightedField,
      trackFieldAction: trackFieldAction,
      uniqueKey: uniqueKey,
      useOnDark: useOnDark,
      uploadedFileAccess: uploadedFileAccess,
      value: value
    }));
  }
  renderLabel() {
    const {
      field,
      LabelComponent,
      allowFieldReset,
      fieldKey,
      isDefaultValue,
      onFieldReset,
      scrollToField
    } = this.props;
    const {
      label,
      type,
      inherited_value
    } = field;
    if (!label) return null;
    return /*#__PURE__*/_jsx(EditFormLabel, {
      allowFieldReset: allowFieldReset,
      fieldKey: fieldKey,
      fieldType: type,
      isDefaultValue: isDefaultValue,
      inheritedValue: inherited_value,
      label: label,
      LabelComponent: LabelComponent,
      onFieldUnlink: () => {
        this.onFieldChange(field.default);
      },
      onFieldReset: onFieldReset,
      scrollToField: scrollToField
    });
  }
  render() {
    const {
      allowDataTokens: doesEditorAllowTokens,
      field,
      fieldKey,
      flush,
      modulePath,
      handleFieldEditLinkOverride,
      isInNewEditor
    } = this.props;
    const {
      allow_data_tokens: doesFieldAllowTokens,
      label,
      labelItemRight,
      help_text: helpText,
      inline_help_text: inlineHelpText,
      links,
      required,
      name,
      shouldOverrideEditUrl,
      scrollToFieldPath
    } = field;
    const {
      error
    } = this.state;
    const hideLabel = hideFieldLabel({
      field,
      modulePath,
      isInNewEditor
    }) && !this.shouldRenderDataToken;
    const renderedLabel = this.renderLabel();
    const areDataTokensAllowed = doesEditorAllowTokens && doesFieldAllowTokens;
    return /*#__PURE__*/_jsx(EditFormControl, {
      allowDataTokens: areDataTokensAllowed,
      "aria-label": label || name,
      "data-field-key": fieldKey.join('.'),
      fieldKey: fieldKey.join('.'),
      error: !!error,
      label: !hideLabel ? renderedLabel : null,
      labelItemRight: labelItemRight,
      onBlur: () => this.onValidateRequiredValue(),
      noLabel: !isInNewEditor && (hideLabel || !label),
      required: required,
      helpText: !hideLabel ? helpText : null,
      inlineHelpText: !hideLabel ? inlineHelpText : null,
      links: !hideLabel && inlineHelpText ? links : null,
      validationMessage: error,
      withPadding: !flush,
      shouldOverrideEditUrl: shouldOverrideEditUrl,
      handleFieldEditLinkOverride: handleFieldEditLinkOverride,
      scrollToFieldPath: scrollToFieldPath,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [this.renderDisabledAlert(), this.renderField(renderedLabel)]
      })
    });
  }
}
EditFormField.defaultProps = {
  fieldSpecificProps: {},
  highlightedField: [],
  readOnly: false,
  validateOnMount: false,
  value: undefined
};
export default EditFormField;